import { Controller } from "@hotwired/stimulus";

// A lightweight HTML tooltip controller inspired by @kanety/stimulus-tooltip
// Keeps the tip element in the DOM so other controllers (e.g. invest-details)
// can continue to update its content dynamically.
//
// L'apparence de la bulle (fond, bordure, ombre, largeur max, rendu des listes)
// vit dans le SCSS (`.st-tooltip__tip`, cf. issue #659) : le contrôleur n'écrit
// en style inline que la géométrie, qui dépend du survol.
//
// Usage:
// <span data-controller="html-tooltip"
//       data-action="mouseover->html-tooltip#show mouseout->html-tooltip#hide focus->html-tooltip#show blur->html-tooltip#hide"
//       data-html-tooltip-position-value="bottom-start"
//       data-html-tooltip-offset-value="8">
//   <span data-html-tooltip-target="content">...</span>
//   <span data-html-tooltip-target="tip" class="d-none">HTML content here</span>
// </span>
//
// Positions: top-start | top-end | bottom-start | bottom-end | left-start | left-end | right-start | right-end
// Shorthands: top | bottom | left | right (mapped to *-start)
export default class extends Controller {
  static targets = ["content", "tip"];
  static values = {
    position: { type: String, default: "bottom-start" },
    offset: { type: Number, default: 8 },
  };

  connect() {
    // Ensure tip is hidden initially but measurable when needed
    if (this.hasTipTarget) {
      this.hide();
    }
    this._visible = false;
    this._onWindowResize = () => {
      if (this._visible) this.#reposition();
    };
    window.addEventListener("resize", this._onWindowResize);
    window.addEventListener("scroll", this._onWindowResize, true);

    // Garde l'affordance (curseur / soulignement) synchronisée avec la
    // présence de contenu dans la bulle. Utile pour les tooltips alimentés
    // par remote-bind (data-remote-text-path) qui peuvent rester vides
    // tant que l'API ne renvoie pas de texte (ex. PTZ / résidence principale).
    if (this.hasTipTarget) {
      this.#updateAffordance();
      try {
        this._tipObserver = new MutationObserver(() => this.#updateAffordance());
        this._tipObserver.observe(this.tipTarget, {
          childList: true,
          characterData: true,
          subtree: true,
        });
      } catch (_) {
        // MutationObserver indisponible → on garde l'état initial.
      }
    }
  }

  disconnect() {
    window.removeEventListener("resize", this._onWindowResize);
    window.removeEventListener("scroll", this._onWindowResize, true);
    if (this._tipObserver) {
      try { this._tipObserver.disconnect(); } catch (_) {}
      this._tipObserver = null;
    }
  }

  show() {
    if (!this.hasTipTarget || !this.hasContentTarget) return;
    // Pas de bulle vide : si remote-bind n'a pas (encore) injecté de contenu,
    // on n'affiche rien au survol.
    if (!this.#hasTipContent()) return;
    const tip = this.tipTarget;
    // Prepare for measurement
    tip.classList.remove("d-none");
    tip.style.visibility = "hidden";
    tip.style.display = "block";
    // Position
    this.#reposition();
    // Reveal
    tip.style.visibility = "visible";
    this._visible = true;
  }

  hide() {
    if (!this.hasTipTarget) return;
    const tip = this.tipTarget;
    tip.style.display = "none";
    tip.classList.add("d-none");
    this._visible = false;
  }

  #reposition() {
    const tip = this.tipTarget;
    const content = this.contentTarget;
    const rect = content.getBoundingClientRect();
    const { width: tipW, height: tipH } = tip.getBoundingClientRect();

    const pos = this.#normalizePosition(this.positionValue);
    const offset = Number(this.offsetValue) || 0;

    // Default to bottom-start
    let top = rect.bottom + offset;
    let left = rect.left;

    const winW = document.documentElement.clientWidth || window.innerWidth;
    const winH = document.documentElement.clientHeight || window.innerHeight;

    if (pos.main === "top") {
      top = rect.top - tipH - offset;
    } else if (pos.main === "bottom") {
      top = rect.bottom + offset;
    } else if (pos.main === "left") {
      top = rect.top;
      left = rect.left - tipW - offset;
    } else if (pos.main === "right") {
      top = rect.top;
      left = rect.right + offset;
    }

    if (pos.main === "top" || pos.main === "bottom") {
      if (pos.align === "end") {
        left = rect.right - tipW;
      } else {
        left = rect.left;
      }
    } else {
      // left/right main axis, align by start/end vertically
      if (pos.align === "end") {
        top = rect.bottom - tipH;
      } else {
        top = rect.top;
      }
    }

    // Auto-adjust if out of viewport
    // Horizontal clamp
    if (left + tipW > winW - 4) left = Math.max(4, winW - tipW - 4);
    if (left < 4) left = 4;
    // Vertical clamp
    if (top + tipH > winH - 4) top = Math.max(4, winH - tipH - 4);
    if (top < 4) top = 4;

    tip.style.position = "fixed";
    tip.style.top = `${Math.round(top)}px`;
    tip.style.left = `${Math.round(left)}px`;
    tip.style.zIndex = "1080";
  }

  #normalizePosition(value) {
    const v = (value || "").toLowerCase().trim();
    // shorthands map to "*-start"
    if (v === "top" || v === "bottom" || v === "left" || v === "right") {
      return { main: v, align: "start" };
    }
    const [main, align] = v.split("-");
    const m = ["top", "bottom", "left", "right"].includes(main) ? main : "bottom";
    const a = ["start", "end"].includes(align) ? align : "start";
    return { main: m, align: a };
  }

  #hasTipContent() {
    if (!this.hasTipTarget) return false;
    // Le texte trimé couvre les bulles remote-bind : tant qu'elles sont vides,
    // on n'affiche rien. Une image seule (colonne Image de la fiche d'import)
    // n'a pas de nœud texte — `alt` ne compte pas dans `textContent` — et doit
    // quand même ouvrir la bulle.
    if ((this.tipTarget.textContent || "").trim().length > 0) return true;
    return this.tipTarget.querySelector("img") != null;
  }

  #updateAffordance() {
    const has = this.#hasTipContent();
    this.element.classList.toggle("html-tooltip--has-content", has);
  }
}


